import { autoLogoutRemaining } from '@ehr/shared';

/**
 * ehr#416 - the auto logout clock (ADMIN_SETTINGS_10-13-V1). Idle means no mouse, key, wheel,
 * scroll or touch in ANY open EHR tab: each tab notes its last activity in localStorage (at most
 * every 5 s) and reads the others'. A sign-out in one tab is announced the same way, so every
 * tab signs out together.
 */
export const ACTIVITY_KEY = 'credify.session.lastActivity';
export const LOGOUT_KEY = 'credify.session.logout';
/** Fired on window just before an auto sign-out: open editors save their unsaved work. */
export const AUTOSAVE_EVENT = 'credify:autosave';
const WRITE_EVERY_MS = 5000;

export type SignOutReason = 'idle' | 'manual' | 'other-tab';

/** The event's detail: a listener pushes its save, resolving to what it saved (or null). */
export interface AutosaveDetail {
  waitFor: Promise<string | null>[];
}

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export type IdleState =
  { kind: 'active' } | { kind: 'warn'; remaining: number } | { kind: 'expired' };

export class IdleSession {
  private lastLocal: number;
  private lastWrite = 0;
  private warned = false;

  constructor(
    private minutes: number,
    private readonly store: KV | null,
    private readonly now: () => number = Date.now,
  ) {
    this.lastLocal = now();
    this.touch(true);
  }

  setMinutes(n: number): void {
    this.minutes = n;
  }

  private shared(): number {
    try {
      return Number(this.store?.getItem(ACTIVITY_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  /** Note activity here; tell the other tabs (always when forced, else at most every 5 s). */
  touch(force = false): void {
    const t = this.now();
    this.lastLocal = t;
    if (!force && t - this.lastWrite < WRITE_EVERY_MS) return;
    this.lastWrite = t;
    try {
      this.store?.setItem(ACTIVITY_KEY, String(t));
    } catch {
      /* storage blocked: this tab still counts its own activity */
    }
  }

  /** Mouse / key / scroll. Once the warning shows, only Stay Signed In keeps the session. */
  activity(): void {
    if (!this.warned) this.touch(false);
  }

  /** Stay Signed In (here or in another tab). */
  stay(): void {
    this.warned = false;
    this.touch(true);
  }

  /** Once a second: where the clock stands. Activity in another tab clears a warning here. */
  tick(): IdleState {
    const last = Math.max(this.lastLocal, this.shared());
    const r = autoLogoutRemaining(this.minutes, last, this.now());
    if (r.expired) return { kind: 'expired' };
    this.warned = r.warn;
    return r.warn ? { kind: 'warn', remaining: r.remaining } : { kind: 'active' };
  }
}

/** Tell the other EHR tabs this one signed out. */
export function announceSignOut(store: KV | null, reason: SignOutReason, now = Date.now()): void {
  try {
    store?.setItem(LOGOUT_KEY, JSON.stringify({ at: now, reason }));
  } catch {
    /* storage blocked */
  }
}

let signedOutHere = false;
/** This tab ended the session itself: the "sign-in has expired" window stays shut. */
export const markSignedOut = (): void => {
  signedOutHere = true;
};
export const isSignedOutHere = (): boolean => signedOutHere;

/** Ask open editors to save, waiting at most `ms`; resolves to what was saved. */
export async function autosaveBeforeSignOut(
  target: Pick<Window, 'dispatchEvent'>,
  ms = 4000,
): Promise<string[]> {
  const detail: AutosaveDetail = { waitFor: [] };
  target.dispatchEvent(new CustomEvent<AutosaveDetail>(AUTOSAVE_EVENT, { detail }));
  if (!detail.waitFor.length) return [];
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), ms));
  const saved = await Promise.all(
    detail.waitFor.map((p) => Promise.race([p.catch(() => null), timeout])),
  );
  return saved.filter((s): s is string => !!s);
}
